import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { themes, colorSchemes, defaultTheme, ThemeMode, ColorScheme, Theme } from './theme';

interface ThemeContextType {
  theme: Theme;
  mode: ThemeMode;
  colorScheme: ColorScheme;
  toggleMode: () => void;
  setColorScheme: (scheme: ColorScheme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('theme-mode');
    return (saved as ThemeMode) || defaultTheme.mode;
  });

  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(() => {
    const saved = localStorage.getItem('color-scheme');
    return (saved as ColorScheme) || defaultTheme.colorScheme;
  });

  // Get current theme with color scheme applied
  const getCurrentTheme = (): Theme => {
    const baseTheme = themes[mode];
    const schemeColors = colorSchemes[colorScheme];
    
    return {
      ...baseTheme,
      primary: schemeColors.primary,
      primaryHover: schemeColors.primaryHover,
      primaryLight: schemeColors.primaryLight,
      textInverse: schemeColors.textInverse,
    };
  };

  const [theme, setTheme] = useState<Theme>(getCurrentTheme());

  // Update theme when mode or color scheme changes
  useEffect(() => {
    setTheme(getCurrentTheme());
  }, [mode, colorScheme]);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('color-scheme', colorScheme);
  }, [colorScheme]);

  // Apply CSS variables to document root
  useEffect(() => {
    const root = document.documentElement;
    
    Object.entries(theme).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });

    // Add theme mode class to body
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${mode}`);
  }, [theme, mode]);

  const toggleMode = () => {
    setMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setColorScheme = (scheme: ColorScheme) => {
    setColorSchemeState(scheme);
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      mode,
      colorScheme,
      toggleMode,
      setColorScheme,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
